import { companyData } from '@/mocks/company';
import type { ProposalMock } from '@/mocks/proposals';
import type { TemplateConfig } from '@/mocks/template-config';

interface PDFVantagensProps {
  proposal: ProposalMock;
  config?: TemplateConfig;
  coverImage: string;
}

export default function PDFVantagens({ proposal, config, coverImage }: PDFVantagensProps) {
  const advantagesText = (proposal as unknown as Record<string, unknown>).advantages_text as string || '';
  const advantagesImage = (proposal as unknown as Record<string, unknown>).advantages_image as string || '';
  const proposalNumber = proposal.proposal_number || proposal.id;

  // Renderiza o texto preservando quebras de linha
  const renderAdvantagesText = () => {
    if (!advantagesText) {
      return (
        <p style={{ color: '#555555', fontSize: '12px', lineHeight: 1.6, fontStyle: 'italic' }}>
          Nenhum texto de vantagens preenchido.
        </p>
      );
    }
    return advantagesText.split('\n').map((line, i) => (
      <p key={i} style={{ color: '#333333', fontSize: '12px', lineHeight: 1.6, margin: '0 0 4px 0' }}>
        {line || <>&nbsp;</>}
      </p>
    ));
  };

  return (
    <div
      style={{
        width: '210mm',
        height: '297mm',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', Arial, sans-serif",
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ===== CABEÇALHO AZUL ===== */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0a1628 0%, #0f1f35 100%)',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              border: '1.5px solid #c9a44a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ color: '#c9a44a', fontSize: '13px', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
              E
            </span>
          </div>
          <div>
            <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: 700 }}>Eletrônica </span>
            <span style={{ color: '#c9a44a', fontSize: '13px', fontWeight: 700 }}>2000</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ color: '#c9a44a', fontSize: '10px', margin: 0, fontWeight: 600 }}>
            PROPOSTA COMERCIAL
          </p>
          <p style={{ color: '#9ca3af', fontSize: '9px', margin: 0 }}>
            Nº {proposalNumber}
          </p>
        </div>
      </div>

      {/* ===== FAIXA DOURADA DECORATIVA ===== */}
      <div style={{ width: '100%', height: '3px', background: 'linear-gradient(90deg, #c9a44a, #e8d08a, #c9a44a)', flexShrink: 0 }} />

      {/* ===== CONTEÚDO PRINCIPAL — área útil com margem de segurança ===== */}
      <div style={{ padding: '18px 24px 25mm 24px', flex: 1, overflow: 'hidden' }}>

        {/* Título da seção */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <div style={{ width: '4px', height: '26px', backgroundColor: '#c9a44a', borderRadius: '2px', flexShrink: 0 }} />
            <div>
              <h2 style={{ color: '#0a1628', fontSize: '18px', fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
                Vantagens do Seu Investimento
              </h2>
              <p style={{ color: '#c9a44a', fontSize: '11px', margin: 0, fontWeight: 500 }}>
                Por que escolher a Eletrônica 2000?
              </p>
            </div>
          </div>
          <div style={{ height: '1px', backgroundColor: '#e8e8e8', marginTop: '8px' }} />
        </div>

        {/* Imagem + Texto lado a lado (se tiver imagem) */}
        {advantagesImage ? (
          <div style={{ display: 'flex', gap: '16px', marginBottom: '14px' }}>
            {/* Imagem */}
            <div style={{ width: '72mm', flexShrink: 0 }}>
              <div style={{ width: '100%', height: '48mm', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e8e8e8' }}>
                <img
                  src={advantagesImage}
                  alt="Ilustração"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                  crossOrigin="anonymous"
                />
              </div>
              {/* Mini dados da proposta abaixo da imagem */}
              <div style={{ marginTop: '8px', backgroundColor: '#f5f7fa', borderRadius: '6px', padding: '8px 10px', border: '1px solid #e8e8e8' }}>
                <p style={{ color: '#888888', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px 0' }}>
                  Proposta para:
                </p>
                <p style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, margin: '0 0 3px 0' }}>
                  {proposal.clientName}
                </p>
                {proposal.clientCompany && (
                  <p style={{ color: '#555555', fontSize: '10px', margin: '0 0 3px 0' }}>{proposal.clientCompany}</p>
                )}
                <div style={{ height: '1px', backgroundColor: '#e0e0e0', margin: '5px 0' }} />
                <p style={{ color: '#555555', fontSize: '10px', margin: 0 }}>
                  Proposta Nº: <strong style={{ color: '#0a1628' }}>{proposalNumber}</strong>
                </p>
              </div>
            </div>

            {/* Texto */}
            <div style={{ flex: 1 }}>
              {renderAdvantagesText()}
            </div>
          </div>
        ) : (
          /* Sem imagem — texto em largura total */
          <div style={{ marginBottom: '14px' }}>
            {/* Dados do cliente em destaque */}
            <div style={{ backgroundColor: '#f5f7fa', borderRadius: '6px', padding: '8px 12px', border: '1px solid #e8e8e8', marginBottom: '10px', display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
              <div>
                <p style={{ color: '#888888', fontSize: '9px', textTransform: 'uppercase', display: 'block', margin: '0 0 2px 0' }}>Cliente</p>
                <p style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, margin: 0 }}>{proposal.clientName}</p>
              </div>
              {proposal.clientCompany && (
                <div>
                  <p style={{ color: '#888888', fontSize: '9px', textTransform: 'uppercase', display: 'block', margin: '0 0 2px 0' }}>Empresa</p>
                  <p style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, margin: 0 }}>{proposal.clientCompany}</p>
                </div>
              )}
              <div>
                <p style={{ color: '#888888', fontSize: '9px', textTransform: 'uppercase', display: 'block', margin: '0 0 2px 0' }}>Proposta Nº</p>
                <p style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, margin: 0 }}>{proposalNumber}</p>
              </div>
            </div>
            {renderAdvantagesText()}
          </div>
        )}

        {/* Imagem de fundo (thumbnail da capa) — decorativa */}
        <div style={{ marginTop: '8px', borderTop: '1px solid #e8e8e8', paddingTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <div style={{ width: '3px', height: '16px', backgroundColor: '#c9a44a', borderRadius: '2px' }} />
            <span style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Por que escolher a Eletrônica 2000
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
            {[
              { icon: '✓', text: 'Mais de 30 anos de experiência no mercado' },
              { icon: '✓', text: 'Equipe técnica especializada e certificada' },
              { icon: '✓', text: 'Garantia em todos os equipamentos instalados' },
              { icon: '✓', text: 'Suporte técnico e atendimento pós-venda' },
              { icon: '✓', text: 'Equipamentos de alta qualidade e durabilidade' },
              { icon: '✓', text: 'Projetos personalizados para cada cliente' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, backgroundColor: '#c9a44a', borderRadius: '50%' }}>
                  <span style={{ color: '#ffffff', fontSize: '10px', fontWeight: 700 }}>{item.icon}</span>
                </div>
                <span style={{ color: '#444444', fontSize: '10px', lineHeight: 1.3 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== RODAPÉ — fixo na parte inferior ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(135deg, #0a1628 0%, #0f1f35 100%)',
          padding: '10px 24px',
          height: '18mm',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '1px solid #c9a44a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#c9a44a', fontSize: '9px', fontWeight: 700, fontFamily: 'Georgia, serif' }}>E</span>
            </div>
            <span style={{ color: '#ffffff', fontSize: '10px', fontWeight: 700 }}>Eletrônica </span>
            <span style={{ color: '#c9a44a', fontSize: '10px', fontWeight: 700 }}>2000</span>
          </div>
          <span style={{ color: '#9ca3af', fontSize: '9px' }}>
            {companyData.phone1} · {companyData.email}
          </span>
        </div>
      </div>
    </div>
  );
}